import { CarrierHierarchyDto, KnownProjectDtoProjectType, ProjectDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { isCarrierProjectType } from '../../../app/navigation/courier-project-type';
import { isCarrierManagementProject } from '../../carriers/logic/carrier-management';

/** What a project's place in the carrier model lets the settings tree offer on it. */
export interface ProjectCapabilities {
  canHaveChildProjects: boolean;
  canHaveHubs: boolean;
  canHaveAccessControl: boolean;
  canHaveRegistrationPages: boolean;
  /** The project carrier organisations are filed under. */
  canAddCarrier: boolean;
  /** A carrier project that already manages an organisation. */
  canAddRegion: boolean;
  /** The organisation's container, which is what servicing grants hang off. */
  carrierOrganisationGroupID?: string;
  /** Where `carriers:Create` is checked, which is never the project being added to. */
  carriersAnchorProjectID?: string;
}

/** An ordinary project: buildings, hubs, kiosks and the settings that go with them. */
export const DEFAULT_PROJECT_CAPABILITIES: ProjectCapabilities = {
  canHaveChildProjects: true,
  canHaveHubs: true,
  canHaveAccessControl: true,
  canHaveRegistrationPages: true,
  canAddCarrier: false,
  canAddRegion: false
};

/**
 * Carrier projects sit outside the building hierarchy: they hold couriers, and appear on kiosks
 * through their organisation's servicing rather than by owning anything. So neither they nor the
 * anchor they are filed under take child projects, hubs or access control.
 *
 * Falls back to an ordinary project while the data loads, so nothing that should be there flickers
 * away — the carrier-only actions are the ones that wait.
 */
export function getProjectCapabilities(project?: ProjectDto, hierarchy?: CarrierHierarchyDto): ProjectCapabilities {
  if (!project) {
    return DEFAULT_PROJECT_CAPABILITIES;
  }

  if (isCarrierProjectType(project.projectType)) {
    return {
      canHaveChildProjects: false,
      canHaveHubs: false,
      canHaveAccessControl: false,
      canHaveRegistrationPages: false,
      canAddCarrier: false,
      // A region's own child would be a sub-region, which has no container to live in.
      canAddRegion: project.projectType === KnownProjectDtoProjectType.Carrier && !!hierarchy?.organisation,
      carrierOrganisationGroupID: hierarchy?.organisation?.id,
      carriersAnchorProjectID: hierarchy?.anchorProject?.id
    };
  }

  if (isCarrierManagementProject(project)) {
    return {
      canHaveChildProjects: false,
      canHaveHubs: false,
      canHaveAccessControl: false,
      canHaveRegistrationPages: false,
      canAddCarrier: true,
      canAddRegion: false,
      carriersAnchorProjectID: project.id
    };
  }

  return DEFAULT_PROJECT_CAPABILITIES;
}
